import React, { useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { useFormik } from "formik";
import * as Yup from "yup";
import ChassisDropdown from "../../../components/common/ChassisDropdown";
import { getCurrencySymbol } from "../../../utils/currencySymbol";
import { Dropdown } from "primereact/dropdown";

const ConvertOrderDialog = ({ visible, onHide, prefill, basket, t, onSubmit, busy }) => {
  const schema = Yup.object({
    description: Yup.string().trim().nullable(),
    invoiceEmail: Yup.string().email(t("errors.InvalidEmail")).nullable(),
    paymentZterm: Yup.string().required(t("order.paymentTermRequired")),
    vorVin: Yup.string().when("isVor", {
      is: true,
      then: (s) => s.required(t("order.chassisNoRequired")),
      otherwise: (s) => s.nullable(),
    }),
  });

  const formik = useFormik({
    validationSchema: schema,
    initialValues: {
      alternativeShippingAddress: "",
      description: "",
      refOrderNo: "",
      bulletNumber: "",
      freightCompany: "",
      accountNumber: "",
      vorVin: "",
      vorVinObj: null,
      invoiceEmail: "",
      selectedAlternativeShipToId: "",
      isServiceBulletin: false,
      isUrgent: false,
      isVor: false,
      paymentZterm: "",
    },
    onSubmit: async (vals) => {
      await onSubmit?.(vals);
    },
  });

  const [selectedShipTo, setSelectedShipTo] = React.useState(null);

  const handleShipToChange = (e) => {
    const selected = e.value;
    setSelectedShipTo(selected);

    if (selected) {
      formik.setFieldValue("selectedAlternativeShipToId", selected.dealerId);
    } else {
      formik.setFieldValue("selectedAlternativeShipToId", "");
    }
  };

  // Bülten siparişi kontrolü
  const isServiceBulletinOrder = 
    (prefill?.data?.isServiceBulletin === true) ||
    (basket?.bulletNumber != null && basket?.bulletNumber !== "");
  
  const bulletNumber = prefill?.data?.bulletNumber || basket?.bulletNumber || "";

  useEffect(() => {
    if (visible) {
      formik.resetForm();
      if (isServiceBulletinOrder) {
        formik.setFieldValue("isServiceBulletin", true);
        formik.setFieldValue("bulletNumber", bulletNumber);
        formik.setFieldValue("isUrgent", false);
        formik.setFieldValue("isVor", false);
      }
      // Varsayılan ödeme tipini ayarla
      if (prefill?.data?.defaultPaymentTerm) {
        formik.setFieldValue("paymentZterm", prefill.data.defaultPaymentTerm);
      }
    }
  }, [visible, isServiceBulletinOrder, bulletNumber, prefill?.data?.defaultPaymentTerm]);

  const clearOtherInputs = (selectedType) => {
    if (selectedType === "service") {
      formik.setFieldValue("freightCompany", "");
      formik.setFieldValue("accountNumber", "");
      formik.setFieldValue("vorVin", "");
      formik.setFieldValue("vorVinObj", null);
    }
    if (selectedType === "urgent") {
      formik.setFieldValue("bulletNumber", "");
    }
    if (selectedType === "vor") {
      formik.setFieldValue("bulletNumber", "");
      formik.setFieldValue("freightCompany", "");
      formik.setFieldValue("accountNumber", "");
    }
  };

  const footer = (
    <div className="flex justify-content-end w-full gap-2">
      <Button
        label={t("common.cancel")}
        icon="pi pi-times"
        className="p-button-outlined"
        onClick={onHide}
        disabled={busy}
      />
      <Button
        label={t("order.finish")}
        icon="pi pi-check"
        onClick={formik.submitForm}
        loading={busy}
      />
    </div>
  );

  return (
    <Dialog
      header={t("order.newOrder")}
      visible={visible}
      onHide={onHide}
      style={{ width: "58rem" }}
      footer={footer}
      modal
      className="p-fluid"
    >
      <div className="grid">
        {/* ÜST: Etiket Bilgileri */}
        <div className="col-12">
          <div className="p-3 border-1 surface-border border-round">
            <div className="mb-2">
              <strong>{t("order.commercialTitle")}:</strong>{" "}
              {selectedShipTo?.commercialTitle ??
                prefill?.data?.commercialTitle ??
                "-"}
            </div>
            <div className="mb-2">
              <strong>{t("order.shippingAddress")}:</strong>{" "}
              {selectedShipTo?.shippingAddress ??
                prefill?.data?.shippingAddress ??
                "-"}
            </div>
            <div className="mb-2">
              <strong>{t("order.region")}:</strong>{" "}
              {selectedShipTo?.region ?? prefill?.data?.region ?? "-"}
            </div>
            <div className="mb-2">
              <strong>{t("order.city")}:</strong>{" "}
              {selectedShipTo?.city ?? prefill?.data?.city ?? "-"}
            </div>
            <div className="mb-2">
              <strong>{t("order.country")}:</strong>{" "}
              {selectedShipTo?.country ?? prefill?.data?.country ?? "-"}
            </div>
            <hr className="my-3" />
            <div className="mb-1">
              <strong>{t("order.retailAmount")}:</strong>{" "}
              {Number(prefill?.data?.retailAmount ?? 0).toFixed(2)}{" "}
              {getCurrencySymbol(prefill?.data?.currency ?? "")}
            </div>
            <div className="mb-2">
              <strong>{t("basket.netTotal")}:</strong>{" "}
              {Number(prefill?.data?.netAmount ?? 0).toFixed(2)}{" "}
              {getCurrencySymbol(prefill?.data?.currency ?? "")}
            </div>
            <hr className="my-3" />
            {/* Ödeme Tipi Dropdown */}
            <div className="field mb-0">
              <label className="font-bold block mb-2">
                {t("order.paymentTerm")} <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={formik.values.paymentZterm}
                options={prefill?.data?.paymentTermOptions ?? []}
                optionLabel="text"
                optionValue="code"
                placeholder={t("order.selectPaymentTerm")}
                onChange={(e) => formik.setFieldValue("paymentZterm", e.value)}
                className={`w-full ${formik.touched.paymentZterm && formik.errors.paymentZterm ? "p-invalid" : ""}`}
              />
              {formik.touched.paymentZterm && formik.errors.paymentZterm && (
                <small className="p-error">{formik.errors.paymentZterm}</small>
              )}
            </div>
          </div>
        </div>

        {/* ALT: Form Alanları */}
        <div className="col-12">
          <div className="grid">
            {/* Adres Seçimi */}
            <div className="col-12">
              <div className="field mb-3">
                <label className="font-bold block mb-2">
                  {t("order.altShippingAddress")}
                </label>
                <Dropdown
                  value={selectedShipTo}
                  options={prefill?.data?.alternativeShipToOptions ?? []}
                  optionLabel="displayName"
                  dataKey="dealerId"
                  placeholder={t("order.selectShippingAddress")}
                  onChange={handleShipToChange}
                  showClear
                  className="w-full"
                />
              </div>
            </div>

            {/* Açıklama */}
            <div className="col-12">
              <div className="field mb-3">
                <label className="font-bold block mb-2">
                  {t("common.description")}
                </label>
                <InputTextarea
                  rows={4}
                  value={formik.values.description}
                  onChange={(e) =>
                    formik.setFieldValue("description", e.target.value)
                  }
                  className={
                    formik.touched.description && formik.errors.description
                      ? "p-invalid"
                      : ""
                  }
                />
                {formik.touched.description && formik.errors.description && (
                  <small className="p-error">
                    {formik.errors.description}
                  </small>
                )}
              </div>
            </div>

            {/* Sipariş No */}
            <div className="col-12 md:col-6">
              <div className="field mb-3">
                <label className="font-bold block mb-2">
                  {t("order.dealerOrderNo")}
                </label>
                <InputText
                  value={formik.values.refOrderNo}
                  onChange={(e) =>
                    formik.setFieldValue("refOrderNo", e.target.value)
                  }
                />
              </div>
            </div>

            {/* === 3 CHECKBOX ALANI === */}
            <div className="col-12">
              <div className="flex gap-4 align-items-center mb-3">
                {/* Servis Bülten - Sadece bülten siparişi ise göster */}
                {isServiceBulletinOrder && (
                  <div className="field-checkbox">
                    <Checkbox
                      inputId="isServiceBulletin"
                      checked={formik.values.isServiceBulletin}
                      onChange={(e) => {
                        if (!isServiceBulletinOrder) {
                          formik.setFieldValue("isServiceBulletin", e.checked);
                          if (e.checked) {
                            formik.setFieldValue("isUrgent", false);
                            formik.setFieldValue("isVor", false);
                            clearOtherInputs("service");
                          } else {
                            formik.setFieldValue("bulletNumber", "");
                          }
                        }
                      }}
                      disabled={isServiceBulletinOrder}
                    />
                    <label htmlFor="isServiceBulletin" className="ml-2">
                      {t("order.serviceBulletin")}
                    </label>
                  </div>
                )}

                {/* Acil - Bülten siparişi değilse göster */}
                {!isServiceBulletinOrder && (
                  <div className="field-checkbox">
                    <Checkbox
                      inputId="isUrgent"
                      checked={formik.values.isUrgent}
                      onChange={(e) => {
                        formik.setFieldValue("isUrgent", e.checked);
                        if (e.checked) {
                          formik.setFieldValue("isVor", false);
                          clearOtherInputs("urgent");
                        } else {
                          formik.setFieldValue("freightCompany", "");
                          formik.setFieldValue("accountNumber", "");
                        }
                      }}
                    />
                    <label htmlFor="isUrgent" className="ml-2">
                      {t("order.urgent")}
                    </label>
                  </div>
                )}

                {/* Yatan Araç - Bülten siparişi değilse göster */}
                {!isServiceBulletinOrder && (
                  <div className="field-checkbox">
                    <Checkbox
                      inputId="isVor"
                      checked={formik.values.isVor}
                      onChange={(e) => {
                        formik.setFieldValue("isVor", e.checked);
                        if (e.checked) {
                          formik.setFieldValue("isUrgent", false);
                          clearOtherInputs("vor");
                        }
                      }}
                    />
                    <label htmlFor="isVor" className="ml-2">
                      {t("order.vor")}
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* SERVİS BÜLTENİ */}
            {formik.values.isServiceBulletin && (
              <div className="col-12 md:col-6">
                <div className="field mb-3">
                  <label className="font-bold block mb-2">
                    {t("order.serviceBulletinNumber")}
                  </label>
                  <InputText
                    value={formik.values.bulletNumber}
                    onChange={(e) =>
                      !isServiceBulletinOrder && formik.setFieldValue("bulletNumber", e.target.value)
                    }
                    readOnly={isServiceBulletinOrder}
                    disabled={isServiceBulletinOrder}
                  />
                </div>
              </div>
            )}

            {/* ACİL */}
            {formik.values.isUrgent && (
              <>
                <div className="col-12 md:col-6">
                  <div className="field mb-3">
                    <label className="font-bold block mb-2">
                      {t("order.freightCompany")}
                    </label>
                    <InputText
                      value={formik.values.freightCompany}
                      onChange={(e) =>
                        formik.setFieldValue("freightCompany", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="col-12 md:col-6">
                  <div className="field mb-3">
                    <label className="font-bold block mb-2">
                      {t("order.accountNumber")}
                    </label>
                    <InputText
                      value={formik.values.accountNumber}
                      onChange={(e) =>
                        formik.setFieldValue("accountNumber", e.target.value)
                      }
                    />
                  </div>
                </div>
              </>
            )}

            {/* ŞASİ NO - Bülten siparişi dışında her zaman göster */}
            {!isServiceBulletinOrder && (
              <div className="col-12">
                <div className="field mb-3">
                  <label className="font-bold block mb-2">
                    {t("order.chassisNo")} {formik.values.isVor && <span className="text-red-500">*</span>}
                  </label>
                  <ChassisDropdown
                    name="vorVin"
                    value={formik.values.vorVin}
                    onChange={async (val, data) => {
                      const id =
                        typeof val === "string"
                          ? val
                          : val?.recordValue ?? val?.value ?? "";
                      await formik.setFieldValue("vorVin", id);
                      formik.setFieldValue("vorVinObj", data ?? null);
                    }}
                    placeholder={t("order.selectChassisNo")}
                  />
                  {formik.touched.vorVin && formik.errors.vorVin && (
                    <small className="p-error">{formik.errors.vorVin}</small>
                  )}
                </div>
              </div>
            )}

            {/* Fatura E-posta */}
            <div className="col-12">
              <div className="field mb-2">
                <label className="font-bold block mb-2">
                  {t("order.invoiceEmail")}
                </label>
                <InputText
                  value={formik.values.invoiceEmail}
                  onChange={(e) =>
                    formik.setFieldValue("invoiceEmail", e.target.value)
                  }
                  className={
                    formik.touched.invoiceEmail && formik.errors.invoiceEmail
                      ? "p-invalid"
                      : ""
                  }
                />
                {formik.touched.invoiceEmail && formik.errors.invoiceEmail && (
                  <small className="p-error">
                    {formik.errors.invoiceEmail}
                  </small>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ConvertOrderDialog;
